import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BedDouble,
  Sofa,
  UtensilsCrossed,
  Wifi,
  ShieldCheck,
  Trees,
  Printer,
  Stethoscope,
  ShoppingBag,
  Pizza,
  Ticket,
  Train,
  TrainFront,
  Plane,
  Bus,
  MapPin,
  Phone,
  Mail,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import { KochiHeader, KochiFooter } from "./MetroKochiChrome";
import Seo from "./Seo";
import { KOCHI } from "../mock";

const KOCHI_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  name: "Morning Star Metro Home Stay Kochi",
  description:
    "Premium whole-house home stay in the heart of Kochi city (Ernakulam), Kerala. Walkable to two Kochi Metro stations, close to Lulu Mall, shopping and cinema. Couple-friendly and family-friendly with 1, 2 and 3 bedroom options, kitchen and parking.",
  url: "https://www.auracresthaven.com/morning_star_metro_home_stay_kochi",
  telephone: "+91-9388881016",
  email: "booking@auracresthaven.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "VRRWA#4, Vylassery Rd, Palarivattom",
    addressLocality: "Kochi",
    addressRegion: "Kerala",
    postalCode: "682025",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 9.994676071313982,
    longitude: 76.30742878465527,
  },
  areaServed: ["Kochi", "Ernakulam", "Palarivattom", "Edappally", "Kaloor"],
  parentOrganization: { "@type": "Organization", name: "Auracrest Haven Resorts" },
  amenityFeature: [
    { "@type": "LocationFeatureSpecification", name: "Free Parking" },
    { "@type": "LocationFeatureSpecification", name: "Fully Equipped Kitchen" },
    { "@type": "LocationFeatureSpecification", name: "High-Speed WiFi" },
    { "@type": "LocationFeatureSpecification", name: "CCTV Security" },
    { "@type": "LocationFeatureSpecification", name: "Walkable to Kochi Metro" },
    { "@type": "LocationFeatureSpecification", name: "Couple Friendly" },
    { "@type": "LocationFeatureSpecification", name: "Family Friendly" },
  ],
};

const BASE = "https://www.auracresthaven.com/images";
const GALLERY = [
  "A2.png", "A11.png", "A1.png?rand=a8d9", "B1.png", "B2.png", "c1.png",
  "c2.png", "d1.png", "/assets/kochi_d3.png", "d4.png", "d2.png", "d5.png", "d6.png",
  "d7.png", "l3.png", "l2.png", "l1.png", "k1.png", "k2.png", "k3.png",
  "k4.png", "k5.png", "k6.png", "f1.png", "f2.png", "f3.png",
].map((f) => (f.startsWith("/") ? f : `${BASE}/${f}`));

const HIGHLIGHTS = [
  { icon: TrainFront, title: "2 Metro Stations Walkable", desc: "JLN Stadium & Palarivattom Metro moments away \u2014 a third station is under construction nearby." },
  { icon: MapPin, title: "Heart of the City", desc: "Centrally located in Kochi, the commercial capital of Kerala \u2014 everything you need is minutes away." },
  { icon: Plane, title: "Airport in ~15 mins", desc: "Kochi International Airport is ~17 km away with pickup & drop-off available on request." },
];

const ROOMS = [
  { icon: BedDouble, title: "Two A/C King-Size Bedrooms", desc: "Private attached restrooms, generous wardrobes, cloth hangers, dustbins, and optimal lighting and ventilation." },
  { icon: BedDouble, title: "One Non-A/C Twin Room", desc: "Versatile layout featuring two cozy single split-beds with full wardrobe and essential amenities." },
  { icon: Sparkles, title: "Thoughtful Details", desc: "Multiple charging points in every room, air fresheners throughout, and wall & digital calendar clocks in the portico." },
  { icon: ShieldCheck, title: "Modern Restrooms", desc: "Fitted with hot water mixing taps, washbasins, clothing hangers, and liquid soap dispensers." },
];

const SPACES = [
  { icon: Sofa, title: "Cozy Drawing Room", desc: "Comfortable couches, a large teapoy, and a Google Android TV with high-speed WiFi for all your entertainment." },
  { icon: UtensilsCrossed, title: "Elegant Dining Hall", desc: "A large 6-chair dining table, culinary shelf, washbasin with handwash dispenser, tissue box, and a backlit mirror." },
  { icon: UtensilsCrossed, title: "Fully Equipped Kitchen", desc: "Induction stove with utensils, refrigerator, large sink and filter water tap. Complimentary tea, coffee & sugar." },
  { icon: Trees, title: "Extended Work & Utility Area", desc: "The kitchen opens to a large, roof-covered outdoor work area for washing large utensils and drying clothes." },
];

const FACILITIES = [
  { icon: Wifi, text: "Seamless Connectivity: High-Gain WiFi covering the entire property, backed by solar power and a dedicated inverter for zero internet downtime." },
  { icon: ShieldCheck, text: "Safety & Parking: Tile-paved private road with covered parking for full-sized SUVs, protected by 24/7 surveillance cameras." },
  { icon: Trees, text: "Serene Verandah: An outdoor space adorned with lush green plants, a vertical garden, and a ceiling fan." },
  { icon: Sparkles, text: "On-Demand Services: Laundry services and an iron box available upon request." },
  { icon: Printer, text: "Upstairs Office Center: Printouts, photocopying, a computer terminal, or video conferencing setups (on request for an extra fee)." },
];

const LOCATION = [
  { no: "01", icon: Stethoscope, title: "World-Class Healthcare", desc: "Near Kochi's top medical centers \u2014 Renai Medicity, Welcare, Welmont, and close to Lakeshore, Lissie, Lourde, Amrita, Sunrise & Craft Hospitals." },
  { no: "02", icon: ShoppingBag, title: "Shopping, Malls & Markets", desc: "Minutes from Lulu Mall, Oberon Mall, Centre Square, Grand Mall and more. Close to Marine Drive, textile showrooms, jewellers and major banks." },
  { no: "03", icon: Pizza, title: "Dining & Entertainment", desc: "100+ restaurants nearby, including 24-hour spots within 100 meters \u2014 KFC, Dominos, Burger King, Subway, plus iconic local favourites." },
  { no: "04", icon: Ticket, title: "Attractions & Wellness", desc: "Near Jawaharlal Nehru Stadium and Vegaland Amusement Park. Walkable to spiritual centers, salons, fitness centers, spas and Ayurveda." },
];

const TRANSIT = [
  { icon: Train, title: "Metro Stations", desc: "Moments from JLN Stadium and Palarivattom Metro; a third station is under construction nearby." },
  { icon: Train, title: "Railway Stations", desc: "Near both Ernakulam North (ERN) and Ernakulam South (ERS) Railway Stations." },
  { icon: Plane, title: "Kochi International Airport", desc: "~17 km / about 15 mins. Airport pickup and drop-off available at extra charge." },
  { icon: Bus, title: "Hubs & Jetties", desc: "Close to Vytilla Hub, Kaloor Bus Stand, KSRTC Stand, Kochi Water Metro Jetty and Cruise Ship Jetty." },
];

const Section = ({ id, className = "", children }) => (
  <section id={id} className={className}>
    <div className="max-w-7xl mx-auto px-5 md:px-8">{children}</div>
  </section>
);

const Card = ({ icon: Icon, title, desc, bg = "bg-white" }) => (
  <div className={`${bg} rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow`}>
    <div className="w-12 h-12 rounded-xl bg-[#fdeaea] flex items-center justify-center mb-5">
      <Icon className="w-6 h-6 text-[#e11414]" />
    </div>
    <h3 className="text-lg font-bold text-[#0f2a44] mb-3">{title}</h3>
    <p className="text-neutral-600 leading-7 text-sm">{desc}</p>
  </div>
);

const MetroKochiPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="top" className="font-sans text-neutral-900 bg-white">
      <Seo
        title="Morning Star Metro Home Stay Kochi | Home Stay near Kochi Metro & Lulu Mall"
        description="Morning Star Metro Home Stay Kochi — a premium whole-house home stay in the heart of Kochi city. Home stay near Palarivattom, Edappally & Kochi Metro, near Lulu Mall, with parking, kitchen and 1/2/3 bedroom options. Safe, couple-friendly & family-friendly."
        keywords="Home Stay near Palarivattom, Home Stay near Kochi Metro, Home Stay near Edappally, Family Home Stay near Kochi Metro, Couple Friendly Home Stay near Kochi Metro, Couple Friendly Home Stay Kochi, Home Stay with parking facility in Kochi City, Home Stay in the center of Kochi city, Home stay with access to shopping and cinema in Kochi, Home stay near Lulu Mall Kochi, 3 bedroom home stay in Kochi city, 2 bedroom home stay in Kochi city, 1 bedroom home stay in Kochi city, home stay with kitchen in Kochi city, safe place to stay for couples in Kochi city, Morning Star Metro Home Stay Kochi"
        canonical="https://www.auracresthaven.com/morning_star_metro_home_stay_kochi"
        jsonLd={KOCHI_JSONLD}
      />
      <KochiHeader />

      {/* Hero */}
      <section className="pt-[100px]">
        <div className="bg-gradient-to-br from-[#0f2a44] to-[#123e63] text-white py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
            <p className="text-[#ffb3b3] text-xs font-semibold tracking-[0.25em] uppercase mb-4">
              Morning Star Metro Home Stay Kochi
            </p>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-6">
              An Urban Oasis in the Heart of Kochi City
            </h1>
            <p className="text-neutral-300 leading-8 max-w-3xl mx-auto mb-8">
              Located in the commercial capital of Kerala, Morning Star Metro Home Stay Kochi blends the
              warmth of traditional hospitality with contemporary comfort. Perfect for business,
              healthcare, education, leisure or a family getaway &mdash; centrally located and
              accessible to literally everything a city has to offer, whether you visit as a tourist
              or for official purposes. With the owner residing on the first floor, enjoy absolute
              peace of mind and personalized assistance throughout your stay.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-[#e11414] hover:bg-[#c20d0d] text-white font-semibold px-8 py-3 rounded-md transition-colors"
              >
                Enquire / Book
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 border border-white/40 hover:bg-white/10 text-white font-semibold px-8 py-3 rounded-md transition-colors"
              >
                Auracrest Haven, Munnar <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Highlights strip */}
        <div className="max-w-7xl mx-auto px-5 md:px-8 -mt-10 relative z-10">
          <div className="grid sm:grid-cols-3 gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-lg flex gap-4 items-start">
                <span className="w-11 h-11 shrink-0 rounded-xl bg-[#fdeaea] flex items-center justify-center">
                  <h.icon className="w-5 h-5 text-[#e11414]" />
                </span>
                <div>
                  <h3 className="font-bold text-[#0f2a44] mb-1">{h.title}</h3>
                  <p className="text-neutral-600 text-sm leading-6">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <Section className="py-16 md:py-20 bg-white">
        <h2 className="text-2xl md:text-3xl font-extrabold text-[#0f2a44] mb-8 text-center">
          Property Gallery
        </h2>
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
          {GALLERY.map((img, i) => (
            <img
              key={i}
              src={img}
              alt={`Morning Star Metro Home Stay Kochi ${i + 1}`}
              loading="lazy"
              className="w-full mb-4 rounded-xl shadow-sm hover:shadow-md transition-shadow break-inside-avoid"
            />
          ))}
        </div>
      </Section>

      {/* Accommodation */}
      <Section id="rooms" className="py-16 md:py-20 bg-neutral-50">
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2a44] mb-3 text-center">
          Accommodation &amp; In-Room Comforts
        </h2>
        <p className="text-neutral-600 text-center max-w-2xl mx-auto mb-12">
          Thoughtfully designed with premium essentials to ensure a seamless experience.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {ROOMS.map((r, i) => (
            <Card key={i} {...r} />
          ))}
        </div>
      </Section>

      {/* Living / Dining */}
      <Section className="py-16 md:py-20 bg-white">
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2a44] mb-3 text-center">
          Living, Dining &amp; Gourmet Spaces
        </h2>
        <p className="text-neutral-600 text-center max-w-2xl mx-auto mb-12">
          Relax and dine in spacious, beautifully curated common areas.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SPACES.map((s, i) => (
            <Card key={i} {...s} bg="bg-neutral-50" />
          ))}
        </div>
      </Section>

      {/* Facilities */}
      <section id="facilities" className="py-16 md:py-20 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2a44] mb-10 text-center">
            Premium Facilities &amp; Business Services
          </h2>
          <div className="space-y-4">
            {FACILITIES.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 flex gap-4 shadow-sm">
                <span className="w-11 h-11 shrink-0 rounded-xl bg-[#fdeaea] flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-[#e11414]" />
                </span>
                <p className="text-neutral-700 leading-7">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <Section id="location" className="py-16 md:py-20 bg-white">
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2a44] mb-3 text-center">
          Unmatched Location &amp; Hyper-Local Convenience
        </h2>
        <p className="text-neutral-600 text-center max-w-3xl mx-auto mb-12">
          The true highlight is its unbeatable, central location. Food &amp; groceries arrive in
          5&ndash;15 minutes via Swiggy, Zomato, Blinkit and Instamart. Uber &amp; Ola arrive in
          minutes, with bus stops and an auto stand right outside.
        </p>
        <div className="grid sm:grid-cols-2 gap-6">
          {LOCATION.map((l, i) => (
            <div key={i} className="bg-neutral-50 rounded-2xl p-7 flex gap-5 hover:shadow-md transition-shadow">
              <div className="text-3xl font-extrabold text-[#e11414] shrink-0">{l.no}</div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <l.icon className="w-5 h-5 text-[#e11414]" />
                  <h3 className="text-lg font-bold text-[#0f2a44]">{l.title}</h3>
                </div>
                <p className="text-neutral-600 leading-7 text-sm">{l.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Transit */}
      <Section className="py-16 md:py-20 bg-neutral-50">
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2a44] mb-10 text-center">
          Seamless Transit Hub Connections
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TRANSIT.map((t, i) => (
            <Card key={i} {...t} />
          ))}
        </div>
      </Section>

      {/* Contact */}
      <section id="contact" className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2a44] mb-10 text-center">
            Enquire &amp; Book Your Stay
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: MapPin, label: "Location", value: KOCHI.location },
              { icon: Phone, label: "Phone", value: KOCHI.phone },
              { icon: Mail, label: "Email", value: KOCHI.email },
            ].map((c, i) => (
              <div key={i} className="bg-neutral-50 rounded-2xl p-6 text-center">
                <div className="w-11 h-11 mx-auto rounded-xl bg-[#fdeaea] flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5 text-[#e11414]" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1">
                  {c.label}
                </p>
                <p className="text-neutral-800 leading-7 text-sm">{c.value}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href={`tel:${KOCHI.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-[#e11414] hover:bg-[#c20d0d] text-white font-semibold px-8 py-3.5 rounded-md transition-colors"
            >
              <Phone className="w-5 h-5" /> Call to Book
            </a>
          </div>

          {/* Google Map (approximate pending Google Business verification) */}
          <div className="mt-12">
            <div className="rounded-2xl overflow-hidden shadow-sm border border-neutral-200">
              <iframe
                title="Morning Star Metro Home Stay Kochi location map"
                src="https://www.google.com/maps?q=Morning+Star+Metro+Home+Stay+Kochi&z=17&output=embed"
                width="100%"
                height="420"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="text-center mt-4">
              <a
                href="https://www.google.com/maps/search/?api=1&query=Morning+Star+Metro+Home+Stay+Kochi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#e11414] hover:text-[#c20d0d] font-semibold text-sm"
              >
                <MapPin className="w-4 h-4" /> Open in Google Maps
              </a>
              <p className="text-xs text-neutral-400 mt-2">
                Exact location of Morning Star Metro Home Stay Kochi.
              </p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-neutral-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3 text-center">
              Popular searches
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                "Home Stay near Palarivattom",
                "Home Stay near Kochi Metro",
                "Home Stay near Edappally",
                "Family Home Stay near Kochi Metro",
                "Couple Friendly Home Stay near Kochi Metro",
                "Couple Friendly Home Stay Kochi",
                "Home Stay with parking facility in Kochi City",
                "Home Stay in the center of Kochi city",
                "Home stay with access to shopping and cinema in Kochi",
                "Home stay near Lulu Mall Kochi",
                "3 bedroom home stay in Kochi city",
                "2 bedroom home stay in Kochi city",
                "1 bedroom home stay in Kochi city",
                "home stay with kitchen in Kochi city",
                "safe place to stay for couples in Kochi city",
              ].map((t) => (
                <span
                  key={t}
                  className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-full px-3 py-1.5"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cross-link CTA */}
      <section className="py-16 md:py-20 bg-[#0f2a44]">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <p className="text-[#ffb3b3] text-xs font-semibold tracking-[0.25em] uppercase mb-4">
            Auracrest Haven Resorts
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
            Also visit our hillock retreat in Munnar
          </h2>
          <p className="text-neutral-300 leading-8 mb-8">
            Auracrest Haven is our luxury homestay nestled on a hillock near Sengulam Dam, Munnar.
            Avail special discounts for guests who stay at both our Kochi &amp; Munnar properties!
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#e11414] hover:bg-[#c20d0d] text-white font-semibold px-8 py-3 rounded-md transition-colors"
          >
            Explore Auracrest Haven, Munnar <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <KochiFooter />
    </div>
  );
};

export default MetroKochiPage;
